import { CalendarPlus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { DateField } from '@/components/ui/date-field'
import { Modal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useCreateLeave } from '../../api'
import { useLeaveTypes } from '../../api/referenceApi'
import { todayIso } from '../../lib/fileExport'
import { Field, ModalFooter } from './Field'
import { apiErrorMessage } from '@/i18n'

/** İş günü sayısı (Pzt–Cum): hafta tatili izin süresinden sayılmaz. Resmi tatiller BE'de ayrıca düşülmez. */
export function workDayCount(a: string, b: string) {
  let n = 0
  for (let d = new Date(`${a}T00:00:00`); d <= new Date(`${b}T00:00:00`); d.setDate(d.getDate() + 1)) {
    const wd = d.getDay()
    if (wd !== 0 && wd !== 6) n += 1
  }
  return n
}

export function IzinTalepModal({ personelId, onClose, onCreated }: { personelId: string; onClose: () => void; onCreated?: () => void }) {
  const { t } = useTranslation()
  const types = useLeaveTypes()
  const [tur, setTur] = useState('')
  const turId = tur || types.data?.[0]?.id || ''
  const [baslangic, setBaslangic] = useState(todayIso())
  const [bitis, setBitis] = useState(todayIso())
  const [aciklama, setAciklama] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const create = useCreateLeave()

  const gun = baslangic && bitis && bitis >= baslangic ? workDayCount(baslangic, bitis) : 0
  const errors = {
    tur: turId ? undefined : t('hr.personel.actions.required'),
    baslangic: baslangic ? undefined : t('hr.personel.actions.required'),
    bitis: !bitis ? t('hr.personel.actions.required') : bitis < baslangic ? t('hr.personel.actions.izin.bitisOnce') : gun === 0 ? t('hr.personel.actions.izin.noWorkDay') : undefined,
  }
  const show = (e?: string) => (submitted ? e : undefined)

  const submit = () => {
    setSubmitted(true)
    if (errors.tur || errors.baslangic || errors.bitis) return
    create.mutate(
      { employeeId: personelId, leaveTypeId: turId, start: baslangic, end: bitis, days: gun, reason: aciklama },
      {
        onSuccess: () => {
          toast.success(t('hr.personel.actions.izin.created'))
          onCreated?.()
          onClose()
        },
        onError: (error) => toast.error(apiErrorMessage(error)),
      },
    )
  }

  return (
    <Modal
      open
      onOpenChange={(v) => !v && onClose()}
      icon={<CalendarPlus />}
      title={t('hr.personel.actions.izin.title')}
      description={t('hr.personel.actions.izin.hint')}
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
      footer={<ModalFooter onCancel={onClose} />}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field id="izin-tur" label={t('hr.personel.izin.tur')} className="sm:col-span-2" error={show(errors.tur)}>
          <Select
            id="izin-tur"
            size="lg"
            className="w-full rounded-xl"
            value={turId}
            onChange={setTur}
            loading={types.isLoading}
            options={(types.data ?? []).map((x) => ({ value: x.id, label: x.name, description: x.requiresDocument ? t('hr.personel.actions.izin.belgeGerekli') : undefined }))}
          />
        </Field>
        <Field id="izin-bas" label={t('hr.personel.izin.baslangic')} error={show(errors.baslangic)}>
          <DateField id="izin-bas" className="h-10 rounded-xl" value={baslangic} onChange={setBaslangic} aria-invalid={!!show(errors.baslangic)} />
        </Field>
        <Field id="izin-bit" label={t('hr.personel.izin.bitis')} error={show(errors.bitis)}>
          <DateField id="izin-bit" className="h-10 rounded-xl" value={bitis} min={baslangic} onChange={setBitis} aria-invalid={!!show(errors.bitis)} />
        </Field>
        <Field id="izin-not" label={t('hr.personel.actions.izin.aciklama')} className="sm:col-span-2">
          <Textarea id="izin-not" rows={3} maxLength={300} className="rounded-xl" value={aciklama} onChange={(e) => setAciklama(e.target.value)} />
        </Field>
      </div>
    </Modal>
  )
}
